import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import TableCell from "../../components/common/ui/TableCell";
import FormContentManager from "../../components/common/form/FormContentManager";
import ReferenceModal from "../../components/approval/ReferenceModal";
import TinyMCEeditor from "../../components/const/TinyMCEeditor";
import DocSummaryCard from "../../components/common/form/DocSummary";
import { getWorkOrderDetail } from "../../api/const";
import PeopleCellLoader from "../../components/common/people/PeopleCellLoader";

export default function WorkOrderNew() {
  const { id: projectId, dns } = useParams();
  const editorRef = useRef(null);
  const [requestPeople, setRequestPeople] = useState([]);
  const [reviewer, setReviewer] = useState([])
  /* -------------------------------
     FORM STATE
  -------------------------------- */
  const [form, setForm] = useState({
    title: "",
    start_date: "",
    end_date: "",
    confirmer: null,
    confirmer_name: "",
    confirmer_company_name: "",
    manualCompanyInput: false,
  });

  /* -------------------------------
     모달 관련
  -------------------------------- */
  const [modalOpen, setModalOpen] = useState(false);
  const [showConfirmerModal, setShowConfirmerModal] = useState(false);
  const [confirmerIds, setConfirmerIds] = useState([]);

  const displayConfirmerName = form.confirmer_name || "-";

  /* -------------------------------
     수정 모드 → 데이터 로드
  -------------------------------- */
  useEffect(() => {
    if (!dns) return;

    const load = async () => {
      try {
        const data = await getWorkOrderDetail(dns);
        const meta = data.meta || {};

        // form 기본값
        setForm((prev) => ({
          ...prev,
          title: data.title || "",
          start_date: meta.start_date || "",
          end_date: meta.end_date || "",
          confirmer: data.approval?.approver_id || null,
          confirmer_name: data.approval?.approver_name || "",
          confirmer_company_name: meta.confirmer_company_name || "",
        }));

        if (data.approval) {
          setConfirmerIds([data.approval.approver_id]);
        }

        // TinyMCE 값 주입
        setTimeout(() => {
          if (editorRef.current) {
            editorRef.current.setContent(meta.instructions || "");
          }
        }, 200);
      } catch (err) {
        console.error("WorkOrder load failed:", err);
      }
    };

    load();
  }, [dns]);

  /* -------------------------------
     저장 Body
  -------------------------------- */
  const getBody = () => {
    const instructions = editorRef.current?.getContent() || "";

    return {
      layout: null,
      values: {
        dns: dns || null,
        doc_type: "order",
        title: form.title,

        // 결재용
        confirmer: form.confirmer || null,
        confirmer_name: form.confirmer_name || "",

        meta: {
          start_date: form.start_date,
          end_date: form.end_date,
          instructions,
          confirmer_company_name: form.confirmer_company_name,
        },
      },
    };
  };

  /* -------------------------------
     렌더링
  -------------------------------- */
  return (
    <FormContentManager
      params={{
        project: projectId,
        module: "const",
        doc_type: "order",
      }}
      getBody={getBody}
      redirectTo={`/project/${projectId}/const/order/`}
      docName="작업지시서"
      docDescribe="새 작업지시서를 작성하세요."
      requestPeople={requestPeople}
      reviewer={reviewer}
      summaryComponent={
        <DocSummaryCard
          title="작업지시서 요약"
          sections={[
            { label: "작업명", value: form.title },
            { label: "승인자", value: displayConfirmerName },
            { label: "시작날짜", value: form.start_date },
            { label: "마감날짜", value: form.end_date },
          ]}
        />
      }
    >
      <div className="">
        {/* ▽ 승인자 선택 모달 */}
        <ReferenceModal
          isOpen={showConfirmerModal}
          onClose={() => setShowConfirmerModal(false)}
          selectedIds={confirmerIds}
          name="확인자 선택"
          onChange={(ids) => {
            const onlyOne = ids.slice(-1);
            setConfirmerIds(onlyOne);
          }}
          onSave={(ids, detailUsers) => {
            const sel = detailUsers?.[0];
            if (sel) {
              const fullName = sel.last_name + sel.first_name;

              setForm((prev) => ({
                ...prev,
                confirmer: sel.id,
                confirmer_name: fullName,
              }));
            }
            return true;
          }}
        />

        {/* -- 입력 테이블 -- */}
        <table className="w-full text-sm border mb-6">
          <tbody>
            {/* 작업명 */}
            <tr>
              <TableCell
                label="작업명"
                required
                editing
                colSpan={3}
                input={
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1"
                    value={form.title}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, title: e.target.value }))
                    }
                  />
                }
              />
            </tr>

            <TableCell
              label="시공 담당자"
              colSpan={3}
              editing
              input={
                <PeopleCellLoader
                  dns={dns}
                  value={requestPeople}
                  onChange={(list) => setRequestPeople(list)}
                />
              }
            />
            <tr>
              {/* 승인자 */}
              <TableCell
                colSpan={3}
                label="승인자"
                editing
                input={
                  <PeopleCellLoader
                    dns={dns}
                    value={reviewer}
                    single
                    onChange={(list) => setReviewer(list)}
                  />
                }
              />
            </tr>

            {/* 날짜 */}
            <tr className="divide-x">
              <TableCell
                label="시작날짜"
                editing
                input={
                  <input
                    type="date"
                    className="border rounded px-2 py-1 w-40"
                    value={form.start_date}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, start_date: e.target.value }))
                    }
                  />
                }
              />

              <TableCell
                label="마감날짜"
                editing
                input={
                  <input
                    type="date"
                    className="border rounded px-2 py-1 w-40"
                    value={form.end_date}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, end_date: e.target.value }))
                    }
                  />
                }
              />
            </tr>
          </tbody>
        </table>

        {/* TinyMCE 영역 */}
        <section className="mt-4">
          <div className="w-full border border-black text-sm ">
            <div className="border-b border-black px-3 py-2 font-semibold bg-gray-100 text-body-bold">
              지시사항
            </div>
            <div className="px-3 py-3">
              <TinyMCEeditor height={600} editorRef={editorRef} />
            </div>
          </div>
        </section>
      </div>
    </FormContentManager>
  );
}
